/**
 * AddToSpaceAction
 *
 * Saves a chat message into a selected Space destination.
 *
 * Primary responsibilities:
 * - Render the Add to Space message action
 * - Load searchable Space root, folder, and sprint destinations
 * - Create Space notes and tasks from chat messages
 *
 * Dependencies:
 * - Space hierarchy loader
 * - Space notes and tasks APIs
 */

'use client'

import { useContext, useEffect, useMemo, useState } from 'react'
import {
  IconCheck,
  IconCheckbox,
  IconFolder,
  IconLayoutDashboard,
  IconLayoutDashboardFilled,
  IconLoader2,
  IconNotes,
  IconPlus
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { ChatbotUIContext } from '@/context/context'
import { getSpacesByWorkspaceId } from '@/db/spaces'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import {
  loadSpaceHierarchy,
  type SpaceHierarchyResult
} from '@/lib/spaces/hierarchy-loader'
import {
  formatSpaceNameWithIcon,
  normalizeSpaceEmojiIcon
} from '@/lib/spaces/icon-emoji'
import {
  dispatchSidekickOpenSpaceItem,
  stripSidekickSpaceItemTags
} from '@/lib/sidekick/space-item-context'
import { invalidateSpaceQueries } from '@/lib/spaces/queries'
import { refreshSpaceSidebar } from '@/lib/spaces/sidebar-refresh'
import { cn } from '@/lib/utils'
import type { Tables } from '@/supabase/types'
import { MESSAGE_ICON_SIZE } from '@/components/messages/message-action-icons'

// --------------

type MessageActionPosition = 'header' | 'footer'

type AddToSpaceMode = 'note' | 'task' | 'both'

type SpaceSummary = Pick<Tables<'spaces'>, 'icon' | 'id' | 'name' | 'workspace_id'>

export type CreatedSpaceNote = Pick<
  Tables<'space_notes'>,
  | 'content'
  | 'created_at'
  | 'folder_id'
  | 'id'
  | 'space_id'
  | 'sprint_id'
  | 'title'
  | 'updated_at'
>

export type CreatedSpaceTask = Pick<
  Tables<'space_tasks'>,
  | 'description'
  | 'folder_id'
  | 'id'
  | 'space_id'
  | 'sprint_id'
  | 'title'
>

export type SpaceNoteDestination = {
  displayPath: string
  id: string
  label: string
  path: string
  spaceId: string
  spaceIcon: string | null
  spaceName: string
  folderId: string | null
  sprintId: string | null
  kind: 'space' | 'folder' | 'sprint'
}

interface AddToSpaceActionProps {
  content: string
  hasNote?: boolean
  iconClassName: string
  open?: boolean
  position: MessageActionPosition
  onOpenChange?: (open: boolean) => void
  onNoteCreated?: (note: CreatedSpaceNote, destination: SpaceNoteDestination) => void
  onTaskCreated?: (task: CreatedSpaceTask, destination: SpaceNoteDestination) => void
}

type CreateSpaceNotePayload = {
  customInstruction: string | null
  folderId: string | null
  messageText: string
  spaceId: string
  sprintId: string | null
}

type CreateSpaceTaskFromMessagePayload = {
  assignToLosi: boolean
  customInstruction: string | null
  folderId: string | null
  messageText: string
  spaceId: string
  sprintId: string | null
  timezone: string | null
}

type MessageNoteResponse = {
  note?: {
    folderId?: string | null
    id?: string
    spaceId?: string
    sprintId?: string | null
    title?: string
  }
}

type MessageTaskResponseTask = {
  description?: string | null
  folderId?: string | null
  folder_id?: string | null
  id?: string
  spaceId?: string
  space_id?: string
  sprintId?: string | null
  sprint_id?: string | null
  title?: string
}

type MessageTaskResponse = MessageTaskResponseTask | {
  task?: MessageTaskResponseTask
}

type CreatedSpaceNoteResponse = Partial<Tables<'space_notes'>> & {
  id?: string
  space_id?: string
  title?: string
}

type CreatedSpaceTaskResponse = Partial<Tables<'space_tasks'>> & {
  id?: string
  space_id?: string
  title?: string
}

const MAX_NOTE_TITLE_LENGTH = 84
const MAX_CUSTOM_MESSAGE_LENGTH = 1_200

const ADD_TO_SPACE_MODES: Array<{
  icon: typeof IconNotes
  label: string
  shortLabel: string
  value: AddToSpaceMode
}> = [
  {
    icon: IconNotes,
    label: 'Add as Note',
    shortLabel: 'Note',
    value: 'note'
  },
  {
    icon: IconCheckbox,
    label: 'Add as Task',
    shortLabel: 'Task',
    value: 'task'
  },
  {
    icon: IconLayoutDashboard,
    label: 'Add as Both',
    shortLabel: 'Both',
    value: 'both'
  }
]

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim()

const cleanTitleCandidate = (value: string) => {
  return normalizeText(
    value
      .replace(/^#{1,6}\s+/, '')
      .replace(/^[-*+]\s+/, '')
      .replace(/^>\s*/, '')
      .replace(/[`*_~[\]()]/g, '')
  )
}

export const createSpaceNoteTitleFromMessage = (content: string) => {
  const cleanedContent = stripSidekickSpaceItemTags(content)
  const firstTextLine =
    cleanedContent
      .split('\n')
      .map(cleanTitleCandidate)
      .find(line => line.length > 0) ?? ''

  if (!firstTextLine) {
    return 'Chat message'
  }

  return firstTextLine.length > MAX_NOTE_TITLE_LENGTH
    ? `${firstTextLine.slice(0, MAX_NOTE_TITLE_LENGTH - 1).trim()}...`
    : firstTextLine
}

const getSprintName = (
  sprint: SpaceHierarchyResult['sprints'][number],
  sprintById: Map<string, SpaceHierarchyResult['sprints'][number]>
) => {
  const sprintLabel = sprint.name || `Sprint ${sprint.sprint_number || ''}`.trim()

  if (!sprint.parent_sprint_id) {
    return sprintLabel || 'Sprint'
  }

  const parentSprint = sprintById.get(sprint.parent_sprint_id)

  if (!parentSprint) {
    return sprintLabel || 'Sprint'
  }

  return `${parentSprint.name || 'Sprint'} / ${sprintLabel || 'Sprint'}`
}

export const buildSpaceNoteDestinations = (
  space: SpaceSummary,
  hierarchy?: SpaceHierarchyResult | null
) => {
  const spaceIcon = normalizeSpaceEmojiIcon(space.icon, '📁')
  const spaceDisplayName = formatSpaceNameWithIcon(space.name, space.icon)
  const destinations: SpaceNoteDestination[] = [
    {
      displayPath: spaceDisplayName,
      id: `space:${space.id}`,
      label: space.name,
      path: spaceDisplayName,
      spaceId: space.id,
      spaceIcon,
      spaceName: space.name,
      folderId: null,
      sprintId: null,
      kind: 'space'
    }
  ]

  if (!hierarchy) {
    return destinations
  }

  destinations.push(
    ...hierarchy.folders.map(folder => ({
      displayPath: `${spaceDisplayName} / ${folder.name}`,
      id: `folder:${folder.id}`,
      label: folder.name,
      path: `${spaceDisplayName} / ${folder.name}`,
      spaceId: space.id,
      spaceIcon,
      spaceName: space.name,
      folderId: folder.id,
      sprintId: null,
      kind: 'folder' as const
    }))
  )

  const sprintById = new Map(hierarchy.sprints.map(sprint => [sprint.id, sprint]))

  destinations.push(
    ...hierarchy.sprints.map(sprint => {
      const sprintName = getSprintName(sprint, sprintById)

      return {
        displayPath: `${spaceDisplayName} / ${sprintName}`,
        id: `sprint:${sprint.id}`,
        label: sprintName,
        path: `${spaceDisplayName} / ${sprintName}`,
        spaceId: space.id,
        spaceIcon,
        spaceName: space.name,
        folderId: null,
        sprintId: sprint.id,
        kind: 'sprint' as const
      }
    })
  )

  return destinations
}

const parseCreatedSpaceNote = (
  payload: CreatedSpaceNoteResponse,
  destination: SpaceNoteDestination,
  fallbackTitle: string
): CreatedSpaceNote => {
  if (!payload.id) {
    // TODO: remove this guard once every Space-note creation endpoint returns
    // the canonical note row.
    throw new Error('Space note was created but the response was incomplete')
  }

  return {
    content: typeof payload.content === 'string' ? payload.content : null,
    created_at:
      typeof payload.created_at === 'string'
        ? payload.created_at
        : new Date().toISOString(),
    folder_id:
      typeof payload.folder_id === 'string' ? payload.folder_id : destination.folderId,
    id: payload.id,
    space_id:
      typeof payload.space_id === 'string' ? payload.space_id : destination.spaceId,
    sprint_id:
      typeof payload.sprint_id === 'string' ? payload.sprint_id : destination.sprintId,
    title: typeof payload.title === 'string' ? payload.title : fallbackTitle,
    updated_at:
      typeof payload.updated_at === 'string'
        ? payload.updated_at
        : new Date().toISOString()
  }
}

const parseCreatedSpaceTask = (
  payload: CreatedSpaceTaskResponse,
  destination: SpaceNoteDestination
): CreatedSpaceTask => {
  if (!payload.id) {
    throw new Error('Space task was created but the response was incomplete')
  }

  return {
    description:
      typeof payload.description === 'string' ? payload.description : null,
    folder_id:
      typeof payload.folder_id === 'string' ? payload.folder_id : destination.folderId,
    id: payload.id,
    space_id:
      typeof payload.space_id === 'string' ? payload.space_id : destination.spaceId,
    sprint_id:
      typeof payload.sprint_id === 'string' ? payload.sprint_id : destination.sprintId,
    title: typeof payload.title === 'string' ? payload.title : 'Space task'
  }
}

const readResponseError = async (response: Response, fallback: string) => {
  const errorText = await response.text().catch(() => '')

  if (!errorText) {
    return fallback
  }

  try {
    const payload = JSON.parse(errorText) as { error?: unknown; message?: unknown }
    const message =
      typeof payload.error === 'string'
        ? payload.error
        : typeof payload.message === 'string'
          ? payload.message
          : null

    return message || fallback
  } catch {
    return errorText
  }
}

const createSpaceNote = async (
  destination: SpaceNoteDestination,
  content: string,
  customInstruction: string | null
) => {
  const payload: CreateSpaceNotePayload = {
    customInstruction,
    folderId: destination.folderId,
    messageText: content,
    spaceId: destination.spaceId,
    sprintId: destination.sprintId
  }

  const response = await authenticatedFetch(
    `/api/spaces/${encodeURIComponent(destination.spaceId)}/notes/from-message`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )

  if (!response.ok) {
    throw new Error(
      await readResponseError(response, 'Failed to add message to Space')
    )
  }

  const responsePayload = (await response.json()) as MessageNoteResponse
  const note = responsePayload.note

  return parseCreatedSpaceNote(
    {
      id: note?.id,
      title: note?.title,
      space_id: note?.spaceId,
      folder_id: note?.folderId,
      sprint_id: note?.sprintId
    },
    destination,
    createSpaceNoteTitleFromMessage(content)
  )
}

const createSpaceTask = async (
  destination: SpaceNoteDestination,
  content: string,
  customInstruction: string | null,
  assignToLosi: boolean
) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null
  const payload: CreateSpaceTaskFromMessagePayload = {
    assignToLosi,
    customInstruction,
    folderId: destination.folderId,
    messageText: content,
    spaceId: destination.spaceId,
    sprintId: destination.sprintId,
    timezone
  }

  const response = await authenticatedFetch(
    `/api/spaces/${encodeURIComponent(destination.spaceId)}/tasks/from-message`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )

  if (!response.ok) {
    throw new Error(await readResponseError(response, 'Failed to create task'))
  }

  const responsePayload = (await response.json()) as MessageTaskResponse
  const task = 'task' in responsePayload && responsePayload.task
    ? responsePayload.task
    : responsePayload

  return parseCreatedSpaceTask(
    {
      id: task.id,
      title: task.title,
      description: task.description,
      space_id: task.space_id ?? task.spaceId,
      folder_id: task.folder_id ?? task.folderId,
      sprint_id: task.sprint_id ?? task.sprintId
    },
    destination
  )
}

const attachNoteToTask = async (
  destination: SpaceNoteDestination,
  task: CreatedSpaceTask,
  note: CreatedSpaceNote
) => {
  const response = await authenticatedFetch(
    `/api/spaces/${encodeURIComponent(destination.spaceId)}/tasks/${encodeURIComponent(task.id)}/linked-items`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        item_id: note.id,
        item_type: 'note',
        source_space_id: destination.spaceId
      })
    }
  )

  if (!response.ok) {
    throw new Error(
      await readResponseError(response, 'Failed to link note to task')
    )
  }
}

const getDestinationIcon = (kind: SpaceNoteDestination['kind']) => {
  if (kind === 'folder') {
    return IconFolder
  }

  if (kind === 'sprint') {
    return IconCheck
  }

  return IconLayoutDashboard
}

const getDestinationKey = (destination: SpaceNoteDestination) =>
  `${destination.spaceId}:${destination.id}`

const getModeSuccessLabel = (mode: AddToSpaceMode) => {
  if (mode === 'task') {
    return 'Added task to'
  }

  if (mode === 'both') {
    return 'Added note and task to'
  }

  return 'Added note to'
}

const getDestinationSearchValue = (destination: SpaceNoteDestination) =>
  `${destination.displayPath} ${destination.path} ${destination.kind}`

// --------------

export function AddToSpaceAction({
  content,
  hasNote = false,
  iconClassName,
  open,
  position,
  onOpenChange,
  onNoteCreated,
  onTaskCreated
}: AddToSpaceActionProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { selectedWorkspace, spaces } = useContext(ChatbotUIContext)
  const [localIsOpen, setLocalIsOpen] = useState(false)
  const [destinations, setDestinations] = useState<SpaceNoteDestination[]>([])
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [creatingDestinationKey, setCreatingDestinationKey] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saveMode, setSaveMode] = useState<AddToSpaceMode>('note')
  const [customInstruction, setCustomInstruction] = useState('')
  const [customInstructionOpen, setCustomInstructionOpen] = useState(false)
  const [assignToLosi, setAssignToLosi] = useState(false)
  const [showCreateSpace, setShowCreateSpace] = useState(false)
  const [newSpaceDescription, setNewSpaceDescription] = useState('')
  const [isCreatingSpace, setIsCreatingSpace] = useState(false)
  const isOpen = open ?? localIsOpen

  const setIsOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setLocalIsOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  }

  const workspaceSpaces = useMemo(() => {
    if (!selectedWorkspace?.id) {
      return []
    }

    return spaces.filter(space => space.workspace_id === selectedWorkspace.id)
  }, [selectedWorkspace?.id, spaces])

  useEffect(() => {
    if (!isOpen || !selectedWorkspace?.id) {
      return
    }

    let isCancelled = false

    const loadDestinations = async () => {
      setIsLoadingDestinations(true)
      setErrorMessage(null)

      try {
        const availableSpaces =
          workspaceSpaces.length > 0
            ? workspaceSpaces
            : await getSpacesByWorkspaceId(selectedWorkspace.id)

        const hierarchyResults = await Promise.allSettled(
          availableSpaces.map(async space => ({
            space,
            hierarchy: await loadSpaceHierarchy(space.id, {
              forceRefresh: true
            })
          }))
        )

        if (isCancelled) {
          return
        }

        const nextDestinations: SpaceNoteDestination[] = []
        let failedHierarchyCount = 0

        hierarchyResults.forEach(result => {
          if (result.status === 'fulfilled') {
            nextDestinations.push(
              ...buildSpaceNoteDestinations(
                result.value.space,
                result.value.hierarchy
              )
            )
            return
          }

          failedHierarchyCount += 1
        })

        if (nextDestinations.length === 0) {
          nextDestinations.push(
            ...availableSpaces.flatMap(space => buildSpaceNoteDestinations(space))
          )
        }

        setDestinations(nextDestinations)

        if (failedHierarchyCount > 0 && nextDestinations.length > 0) {
          setErrorMessage('Some folders could not be loaded. Space destinations are still available.')
        }
      } catch {
        if (!isCancelled) {
          setDestinations([])
          setErrorMessage('Spaces could not be loaded. Try again.')
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingDestinations(false)
        }
      }
    }

    void loadDestinations()

    return () => {
      isCancelled = true
    }
  }, [isOpen, selectedWorkspace?.id, workspaceSpaces])

  const handleSelectDestination = async (destination: SpaceNoteDestination) => {
    if (isCreating) {
      return
    }

    setCreatingDestinationKey(getDestinationKey(destination))
    setIsCreating(true)
    setErrorMessage(null)

    try {
      const trimmedInstruction = customInstruction.trim() || null
      const shouldCreateNote = saveMode === 'note' || saveMode === 'both'
      const shouldCreateTask = saveMode === 'task' || saveMode === 'both'
      let createdNote: CreatedSpaceNote | null = null
      let createdTask: CreatedSpaceTask | null = null

      createdTask = shouldCreateTask
        ? await createSpaceTask(
            destination,
            content,
            trimmedInstruction,
            assignToLosi
          )
        : null

      try {
        createdNote = shouldCreateNote
          ? await createSpaceNote(destination, content, trimmedInstruction)
          : null
      } catch (noteError) {
        if (!createdTask) {
          throw noteError
        }

        const message =
          noteError instanceof Error
            ? noteError.message
            : 'The task was created, but the note could not be created.'

        toast.warning(message)
      }

      if (createdNote && createdTask) {
        try {
          await attachNoteToTask(destination, createdTask, createdNote)
        } catch {
          toast.warning('Created note and task, but could not link them.')
        }
      }

      if (createdNote) {
        onNoteCreated?.(createdNote, destination)
      }

      if (createdTask) {
        onTaskCreated?.(createdTask, destination)
      }

      refreshSpaceSidebar(destination.spaceId)
      void invalidateSpaceQueries(queryClient, {
        spaceId: destination.spaceId,
        workspaceId: selectedWorkspace?.id ?? null
      })

      setIsOpen(false)

      if (createdTask) {
        openCreatedSpaceTask(createdTask)
      } else if (createdNote) {
        openCreatedSpaceNote(createdNote)
      }

      const actualSaveMode: AddToSpaceMode =
        createdNote && createdTask ? 'both' : createdTask ? 'task' : 'note'

      toast.success(`${getModeSuccessLabel(actualSaveMode)} ${destination.displayPath}`)
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Could not add this message to Space. Try again.'

      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsCreating(false)
      setCreatingDestinationKey(null)
    }
  }

  const handleCreateSpace = async () => {
    if (!selectedWorkspace?.id || !newSpaceDescription.trim()) return

    setIsCreatingSpace(true)
    setErrorMessage(null)

    try {
      // Generate a space draft from the description
      const draftRes = await authenticatedFetch('/api/spaces/generate-space-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userDescription: newSpaceDescription.trim(),
          workspaceId: selectedWorkspace.id,
          workspaceName: selectedWorkspace.name ?? ''
        })
      })

      let spaceName = newSpaceDescription.trim().slice(0, 50)
      let spaceDescription = newSpaceDescription.trim()
      let spaceIcon = '📁'

      if (draftRes.ok) {
        const draft = (await draftRes.json()) as {
          name?: string
          description?: string
          icon?: string
        }
        if (draft.name) spaceName = draft.name
        if (draft.description) spaceDescription = draft.description
        if (draft.icon) spaceIcon = draft.icon
      }

      // Create the space
      const createRes = await authenticatedFetch('/api/spaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: selectedWorkspace.id,
          name: spaceName,
          description: spaceDescription,
          icon: spaceIcon
        })
      })

      if (!createRes.ok) {
        throw new Error(await readResponseError(createRes, 'Failed to create Space'))
      }

      const newSpace = (await createRes.json()) as Tables<'spaces'>

      // Build destination and add to list
      const newDestination: SpaceNoteDestination = {
        displayPath: formatSpaceNameWithIcon(newSpace.name, newSpace.icon),
        id: `space:${newSpace.id}`,
        label: newSpace.name,
        path: formatSpaceNameWithIcon(newSpace.name, newSpace.icon),
        spaceId: newSpace.id,
        spaceIcon: normalizeSpaceEmojiIcon(newSpace.icon, '📁'),
        spaceName: newSpace.name,
        folderId: null,
        sprintId: null,
        kind: 'space'
      }

      setDestinations(prev => [newDestination, ...prev])
      setShowCreateSpace(false)
      setNewSpaceDescription('')
      toast.success(t('Space created! Select it to add your message.'))

      refreshSpaceSidebar(newSpace.id)
      void invalidateSpaceQueries(queryClient, {
        spaceId: newSpace.id,
        workspaceId: selectedWorkspace.id
      })
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Could not create Space. Try again.'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsCreatingSpace(false)
    }
  }

  const icon = isCreating ? (
    <IconLoader2 className="animate-spin" size={MESSAGE_ICON_SIZE} />
  ) : hasNote ? (
    <IconLayoutDashboardFilled size={MESSAGE_ICON_SIZE} />
  ) : (
    <IconLayoutDashboard size={MESSAGE_ICON_SIZE} />
  )
  const buttonLabel = hasNote ? t('Added to Space') : t('Add to Space')

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'hover:bg-accent inline-flex cursor-pointer items-center justify-center rounded-md transition-colors disabled:cursor-default disabled:opacity-70',
            position === 'header' && 'p-1',
            position === 'footer' &&
              'size-9 shrink-0 touch-manipulation rounded-lg transition-colors active:scale-[0.98]',
            iconClassName,
            hasNote &&
              'border border-amber-400/30 bg-amber-400/10 text-amber-600 hover:bg-amber-400/15 dark:text-amber-300'
          )}
          aria-label={buttonLabel}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          disabled={isCreating}
          title={buttonLabel}
        >
          {icon}
          <span className="sr-only">{buttonLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className="flex max-h-[76vh] w-[min(calc(100vw-2rem),420px)] flex-col overflow-hidden p-0 sm:max-h-[560px]"
      >
        <div className="border-b p-2">
          <div className="bg-muted/50 grid grid-cols-3 gap-1 rounded-lg p-1">
            {ADD_TO_SPACE_MODES.map(mode => {
              const ModeIcon = mode.icon
              const selected = saveMode === mode.value

              return (
                <button
                  key={mode.value}
                  type="button"
                  className={cn(
                    'inline-flex min-w-0 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors sm:text-xs',
                    selected
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                  )}
                  aria-pressed={selected}
                  disabled={isCreating}
                  onClick={() => setSaveMode(mode.value)}
                >
                  <ModeIcon className="size-3.5 shrink-0" aria-hidden="true" />
                  <span className="hidden truncate sm:inline">{t(mode.label)}</span>
                  <span className="truncate sm:hidden">{t(mode.shortLabel)}</span>
                </button>
              )
            })}
          </div>
          <div className="mt-2">
            {customInstructionOpen ? (
              <div className="space-y-2">
                <Textarea
                  value={customInstruction}
                  maxLength={MAX_CUSTOM_MESSAGE_LENGTH}
                  rows={2}
                  placeholder={t('Add a short note for how this should be saved.')}
                  className="min-h-16 resize-none text-xs"
                  disabled={isCreating}
                  onChange={event => setCustomInstruction(event.target.value)}
                />
                <div className="flex items-center justify-between gap-3">
                  {saveMode !== 'note' ? (
                    <label className="text-muted-foreground flex min-w-0 items-center gap-2 text-xs">
                      <Checkbox
                        checked={assignToLosi}
                        disabled={isCreating}
                        onCheckedChange={checked => setAssignToLosi(checked === true)}
                      />
                      <span className="min-w-0">
                        {t('Ask Losi to continue if this needs more work.')}
                      </span>
                    </label>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {customInstruction.length}/{MAX_CUSTOM_MESSAGE_LENGTH}
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground shrink-0 text-xs"
                    disabled={isCreating}
                    onClick={() => setCustomInstructionOpen(false)}
                  >
                    {t('Done')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors"
                disabled={isCreating}
                onClick={() => setCustomInstructionOpen(true)}
              >
                <IconPlus className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {customInstruction
                    ? t('Edit custom message')
                    : t('Add custom message')}
                </span>
              </button>
            )}
          </div>
        </div>
        {showCreateSpace ? (
          <div className="border-b p-3">
            <div className="space-y-2">
              <label className="text-xs font-medium">{t('Describe your new Space')}</label>
              <Textarea
                value={newSpaceDescription}
                onChange={event => setNewSpaceDescription(event.target.value)}
                placeholder={t('e.g. Marketing campaign tracker, Product roadmap, Client project...')}
                className="min-h-16 resize-none text-xs"
                maxLength={500}
                disabled={isCreatingSpace}
                autoFocus
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground text-xs"
                  onClick={() => {
                    setShowCreateSpace(false)
                    setNewSpaceDescription('')
                  }}
                  disabled={isCreatingSpace}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="bg-primary text-primary-foreground inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  onClick={() => void handleCreateSpace()}
                  disabled={isCreatingSpace || !newSpaceDescription.trim()}
                >
                  {isCreatingSpace ? (
                    <IconLoader2 className="size-3 animate-spin" />
                  ) : (
                    <IconPlus className="size-3" />
                  )}
                  {isCreatingSpace ? t('Creating...') : t('Create Space')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <Command className="min-h-0 flex-1">
          <CommandInput placeholder={t('Search Spaces and folders...')} />
          <CommandList className="max-h-[48vh] sm:max-h-80">
            <CommandEmpty>
              {isLoadingDestinations
                ? t('Loading Spaces...')
                : t('No Space destinations found.')}
            </CommandEmpty>
            {!showCreateSpace && (
              <CommandGroup>
                <CommandItem
                  value="__create_new_space__"
                  onSelect={() => setShowCreateSpace(true)}
                  className="min-h-11 gap-2"
                >
                  <IconPlus className="shrink-0" size={16} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{t('Create new Space')}</div>
                    <div className="text-muted-foreground text-xs">
                      {t('Describe it and we\'ll set it up for you')}
                    </div>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={t('Add to Space')}>
              {isLoadingDestinations ? (
                <div className="text-muted-foreground flex items-center gap-2 px-2 py-3 text-sm">
                  <IconLoader2 className="animate-spin" size={16} />
                  {t('Loading destinations...')}
                </div>
              ) : null}
              {destinations.map(destination => {
                const DestinationIcon = getDestinationIcon(destination.kind)
                const destinationKey = getDestinationKey(destination)
                const isCreatingDestination =
                  creatingDestinationKey === destinationKey
                const showSpaceEmojiIcon =
                  destination.kind === 'space' && destination.spaceIcon

                return (
                  <CommandItem
                    key={destinationKey}
                    value={getDestinationSearchValue(destination)}
                    disabled={isCreatingDestination}
                    onSelect={() => void handleSelectDestination(destination)}
                    className="min-h-11 gap-2"
                  >
                    {showSpaceEmojiIcon ? (
                      <span className="w-4 shrink-0 text-center text-base leading-none">
                        {destination.spaceIcon}
                      </span>
                    ) : (
                      <DestinationIcon className="shrink-0" size={16} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {destination.label}
                      </div>
                      {destination.kind !== 'space' ? (
                        <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
                          {destination.spaceIcon ? (
                            <span className="shrink-0 text-xs leading-none">
                              {destination.spaceIcon}
                            </span>
                          ) : null}
                          <span className="truncate">{destination.displayPath}</span>
                        </div>
                      ) : null}
                    </div>
                    {isCreatingDestination ? (
                      <IconLoader2
                        className="text-muted-foreground ml-2 shrink-0 animate-spin"
                        size={14}
                      />
                    ) : null}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {errorMessage ? (
          <div className="text-destructive border-t px-3 py-2 text-xs" role="status">
            {t(errorMessage)}
          </div>
        ) : null}
        {!selectedWorkspace?.id ? (
          <div className="text-muted-foreground border-t px-3 py-2 text-xs" role="status">
            {t('Select a workspace before adding this message to a Space.')}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

export function openCreatedSpaceNote(note: CreatedSpaceNote) {
  dispatchSidekickOpenSpaceItem({
    spaceId: note.space_id,
    type: 'note',
    id: note.id,
    title: note.title,
    summary: null
  })
}

export function openCreatedSpaceTask(task: CreatedSpaceTask) {
  dispatchSidekickOpenSpaceItem({
    spaceId: task.space_id,
    type: 'task',
    id: task.id,
    title: task.title,
    summary: task.description
  })
}
